import { Box, Button, Stack, useTheme } from "@mui/material";
import { Plus } from "lucide-react";
import { type FC, useState } from "react";
import { EditAllocationDrawer } from "web_myplann/Goals/Components/EditAllocationDrawer";
import { GoalDetailSolutionItem } from "web_myplann/Goals/Components/GoalDetailSolutionItem";
import { GoalDetailSolutionItemSkeleton } from "web_myplann/Goals/Components/GoalDetailSolutionItemSkeleton";
import { PinContractChoiceDrawer } from "web_myplann/Goals/Components/PinContract/PinContractChoiceDrawer";
import { PinExistingContractDrawer } from "web_myplann/Goals/Components/PinContract/PinExistingContractDrawer";
import { PinFutureContractDrawer } from "web_myplann/Goals/Components/PinContract/PinFutureContractDrawer";
import { useFormatGoalAmount } from "web_myplann/Goals/Helpers/formatGoalAmount";
import type { GoalDetail } from "web_myplann/Goals/Types/GoalDetail";
import type {
	GoalDetailResponseDTO,
	GoalPortfolioListItemResponseDTO,
	GoalPortfolioResponseDTO,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { CollapsibleSection } from "web_myplann/Shared/Components/CollapsibleSection";

export type PinStep = "closed" | "choose" | "existing" | "future";

type Props = {
	goal: GoalDetail;
	goalDto: GoalDetailResponseDTO | undefined;
	allocatedPortfolios: GoalPortfolioResponseDTO[];
	pinStep: PinStep;
	onPinStepChange: (next: PinStep) => void;
	onPinSelect: (picked: GoalPortfolioListItemResponseDTO) => void;
	onUnpin: (portfolioId: number) => void;
	onEditAllocation: (
		portfolioId: number,
		usagePercent: number,
		options?: { onSettled?: () => void },
	) => void;
	unpinningId: number | null;
};

export const GoalDetailSolution: FC<Props> = ({
	goal,
	goalDto,
	allocatedPortfolios,
	pinStep,
	onPinStepChange,
	onPinSelect,
	onUnpin,
	onEditAllocation,
	unpinningId,
}) => {
	const theme = useTheme();
	const { t } = useResource();
	const formatAmount = useFormatGoalAmount();

	const [editingAllocation, setEditingAllocation] =
		useState<GoalPortfolioResponseDTO | null>(null);
	const closePin = () => onPinStepChange("closed");
	const closeEdit = () => setEditingAllocation(null);

	return (
		<CollapsibleSection
			title={t(Resources.Goals.Detail.Solution.title, {
				amount: formatAmount(
					Math.max(0, goal.savingsTarget - goal.coveredAmount),
				),
			})}
		>
			<Stack bgcolor={theme.palette.background.paper}>
				{allocatedPortfolios.map((portfolio, i, arr) =>
					portfolio.portfolioId === unpinningId ? (
						<GoalDetailSolutionItemSkeleton
							key={portfolio.portfolioId}
							hasDivider={i < arr.length - 1}
						/>
					) : (
						<GoalDetailSolutionItem
							key={portfolio.portfolioId}
							portfolio={portfolio}
							hasDivider={i < arr.length - 1}
							dueYear={goal.dueYear}
							onUnpin={onUnpin}
							onEditAllocation={setEditingAllocation}
						/>
					),
				)}
				<Box px={1.6} py={1} borderTop={1} borderColor="surface.border">
					<Button
						variant="inlineAction"
						disableRipple
						onClick={() => onPinStepChange("choose")}
					>
						<Plus size={14} />
						{t(Resources.Goals.Detail.Solution.pinContract)}
					</Button>
				</Box>
			</Stack>
			<PinContractChoiceDrawer
				open={pinStep === "choose"}
				onClose={closePin}
				onPickExisting={() => onPinStepChange("existing")}
				onPickFuture={() => onPinStepChange("future")}
			/>
			<PinExistingContractDrawer
				open={pinStep === "existing"}
				onClose={closePin}
				onSelect={onPinSelect}
				onAddNew={() => onPinStepChange("future")}
				goalPortfolios={goalDto?.portfolios ?? []}
			/>
			<PinFutureContractDrawer
				open={pinStep === "future"}
				onClose={closePin}
				onSubmit={closePin}
			/>
			<EditAllocationDrawer
				open={editingAllocation !== null}
				portfolio={editingAllocation}
				onClose={closeEdit}
				onSubmit={onEditAllocation}
			/>
		</CollapsibleSection>
	);
};
